import type { CSSProperties } from 'react';
import type { AgeProfile } from '@shh/design-tokens';

// Age profile, device context and zoom are plain data attributes / sizes on <html>; the design
// tokens react to them (see @shh/design-tokens). No React context needed for styling.

/**
 * Size on this device only (the tablet on the wall is read from across the room, a phone from
 * up close). Every token is in rem, so the root font size scales the whole app. Keep the key
 * and the levels in sync with the inline script in index.html (applied before first paint).
 */
const ZOOM_KEY = 'shh.zoom';
export const ZOOM_LEVELS = [80, 90, 100, 110, 120] as const;
export type Zoom = (typeof ZOOM_LEVELS)[number];

export function getZoom(): Zoom {
  try {
    const z = Number(localStorage.getItem(ZOOM_KEY));
    return (ZOOM_LEVELS as readonly number[]).includes(z) ? (z as Zoom) : 100;
  } catch {
    return 100;
  }
}

export function setZoom(zoom: Zoom) {
  try {
    if (zoom === 100) localStorage.removeItem(ZOOM_KEY);
    else localStorage.setItem(ZOOM_KEY, String(zoom));
  } catch {
    // Private mode etc. – the size then lasts for this session only.
  }
  document.documentElement.style.fontSize = zoom === 100 ? '' : `${zoom}%`;
}

/** Adults and teens share the default scale; kids get larger type and targets. */
export function setAgeProfile(profile: AgeProfile | undefined) {
  applyAttr('data-profile', profile === 'teen' ? undefined : profile);
}

export function setDeviceContext(context: 'wall' | undefined) {
  applyAttr('data-context', context);
}

function applyAttr(name: string, value: string | undefined) {
  const html = document.documentElement;
  if (value) html.setAttribute(name, value);
  else html.removeAttribute(name);
}

/** For `columns-cards`: never more columns than cards, so few cards fill the width. */
export const cardColumns = (cards: number) => ({ '--cards': Math.max(1, cards) }) as CSSProperties;
